import { useEffect, useState, type KeyboardEvent, type RefObject } from 'react'
import { twMerge } from 'tailwind-merge'
import { useReminderActions, type ReminderItem } from '../../../hooks/use-reminders'
import { isCaretOnLastLine } from '../utils/caret-line'

interface ReminderTitleProps {
  item: ReminderItem
  /** Names the surface on the update this sends. */
  trigger: string
  /** After a change the host wants to flash as saved. */
  onSaving?: () => void
  fieldRef?: RefObject<HTMLTextAreaElement>
  /** Arrow Down on the title's last line, where the host has notes below. */
  onArrowDownOut?: () => void
  className?: string
}

/**
 * A reminder's title, edited in place. Enter and blur save, Escape puts it
 * back. Shared by the row's popover and the Reminder View, so a reminder reads
 * and edits the same either side.
 */
export function ReminderTitle({ item, trigger, onSaving, fieldRef, onArrowDownOut, className }: ReminderTitleProps) {
  const { updateItem } = useReminderActions()
  const [draft, setDraft] = useState(item.title)

  useEffect(() => setDraft(item.title), [item.title])

  const save = () => {
    const next = draft.trim()
    if (!next || next === item.title) {
      setDraft(item.title)
      return
    }
    updateItem(item.id, next, undefined, { trigger })
    onSaving?.()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      event.currentTarget.blur()
    } else if (event.key === 'Escape') {
      // Only the edit is cancelled; the surface around it stays open.
      event.preventDefault()
      event.stopPropagation()
      setDraft(item.title)
      // Blurring saves, so it waits for the reverted draft to land first.
      const field = event.currentTarget
      requestAnimationFrame(() => field.blur())
    } else if (
      onArrowDownOut &&
      event.key === 'ArrowDown' &&
      !event.shiftKey &&
      isCaretOnLastLine(event.currentTarget)
    ) {
      event.preventDefault()
      onArrowDownOut()
    }
  }

  return (
    <textarea
      ref={fieldRef}
      aria-label="Reminder title"
      placeholder="Reminder"
      value={draft}
      rows={1}
      onChange={(event) => setDraft(event.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={save}
      // Grows with the text, so a sentence-long title wraps instead of scrolling. No focus ring: like the
      // notes under it, the caret is the focus state.
      className={twMerge(
        'text-foreground type-prose-md placeholder:text-foreground-placeholder w-full shrink-0 resize-none break-words bg-transparent p-0 font-medium [field-sizing:content] focus-visible:outline-none',
        className,
      )}
    />
  )
}
